import { ReactElement, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  tradingAPI,
  TradingSectionListResponse,
  TradingArticleSummary,
} from '../../services/api';
import {
  ArrowLeft,
  Loader2,
  Activity,
  TrendingUp,
  TrendingDown,
  Zap,
  BarChart3,
  Target,
  Cpu,
} from 'lucide-react';
import BookmarkButton from '../../components/BookmarkButton';
import BookmarkBatchProvider from '../../components/BookmarkBatchProvider';
import Pagination from '../../components/Pagination';

const SECTION_CONFIG: Record<string, { name: string; icon: ReactElement; bgColor: string }> = {
  breakouts: {
    name: 'Breakouts & Highs',
    icon: <TrendingUp className="w-5 h-5" />,
    bgColor: 'bg-emerald-500',
  },
  breakdowns: {
    name: 'Breakdowns & Lows',
    icon: <TrendingDown className="w-5 h-5" />,
    bgColor: 'bg-red-500',
  },
  momentum: {
    name: 'Momentum Signals',
    icon: <Zap className="w-5 h-5" />,
    bgColor: 'bg-amber-500',
  },
  gaps: {
    name: 'Volume & Price Gaps',
    icon: <BarChart3 className="w-5 h-5" />,
    bgColor: 'bg-blue-500',
  },
  targets: {
    name: 'Analyst Targets',
    icon: <Target className="w-5 h-5" />,
    bgColor: 'bg-purple-500',
  },
  advanced: {
    name: 'Advanced Signals',
    icon: <Cpu className="w-5 h-5" />,
    bgColor: 'bg-slate-600',
  },
};

const RATING_BADGE_COLORS: Record<string, string> = {
  strong_bullish: 'bg-emerald-100 text-emerald-700',
  bullish: 'bg-green-50 text-green-600',
  neutral: 'bg-gray-100 text-gray-600',
  bearish: 'bg-orange-50 text-orange-600',
  strong_bearish: 'bg-red-100 text-red-700',
};


function RatingBadge({ rating }: { rating: string | null }): ReactElement | null {
  if (!rating) return null;
  const colors = RATING_BADGE_COLORS[rating] || 'bg-gray-100 text-gray-600';
  const label = rating.replace(/_/g, ' ');
  return (
    <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded ${colors}`}>
      {label}
    </span>
  );
}

function formatReturn(value: number | null): string {
  if (value == null) return '—';
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
}

function returnColor(value: number | null): string {
  if (value == null) return 'text-gray-400';
  return value >= 0 ? 'text-emerald-600' : 'text-red-500';
}

const STUDY_TYPE_LABELS: Record<string, string> = {
  high_52w: '52W High',
  low_52w: '52W Low',
  sma_reclaim: 'SMA Reclaim',
  drawdown_20: '20% Drawdown',
  correction_10: '10% Correction',
  golden_cross: 'Golden Cross',
  death_cross: 'Death Cross',
  macd_bull_cross: 'MACD Bull',
  macd_bear_cross: 'MACD Bear',
  rsi_overbought: 'RSI Overbought',
  rsi_oversold: 'RSI Oversold',
  momentum_surge: 'Mom. Surge',
  momentum_crash: 'Mom. Crash',
  volume_spike: 'Volume Spike',
  price_gap_up: 'Gap Up',
  price_gap_down: 'Gap Down',
  volatility_spike: 'Vol Spike',
  atr_breakout: 'ATR Breakout',
  target_discount: 'Target Discount',
  target_overshoot: 'Target Overshoot',
  win_streak: 'Win Streak',
};

function studyTypeLabel(studyType: string): string {
  return STUDY_TYPE_LABELS[studyType] || studyType.replace(/_/g, ' ');
}

function ArticleRow({ article }: { article: TradingArticleSummary }): ReactElement {
  const articleHref = tradingAPI.getArticleHtmlUrl(article.id);
  return (
    <div className="group relative bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md hover:border-gray-300 transition-all duration-200">
      <a href={articleHref} className="block">
        {/* Row 1: Symbol + Study Type + Rating */}
        <div className="flex items-center gap-2 mb-1.5 pr-8">
          {article.primary_symbol && (
            <span className="text-base font-mono font-bold text-gray-900">{article.primary_symbol}</span>
          )}
          <span className="text-[10px] font-medium uppercase tracking-wide text-gray-400">
            {studyTypeLabel(article.study_type)}
          </span>
          <div className="ml-auto">
            <RatingBadge rating={article.signal_rating} />
          </div>
        </div>

        {/* Headline */}
        <h3 className="text-sm font-semibold text-gray-800 group-hover:text-gray-900 transition-colors leading-snug line-clamp-1 mb-1.5" style={{ fontFamily: 'Georgia, serif' }}>
          {article.title}
        </h3>

        {/* Bottom row: metrics */}
        <div className="flex items-center gap-2 text-[11px] font-mono text-gray-400">
          {article.avg_forward_return_1m != null && (
            <span className={returnColor(article.avg_forward_return_1m)}>
              {formatReturn(article.avg_forward_return_1m)} 1m
            </span>
          )}
          {article.avg_forward_return_3m != null && (
            <>
              {article.avg_forward_return_1m != null && <span>&middot;</span>}
              <span className={returnColor(article.avg_forward_return_3m)}>
                {formatReturn(article.avg_forward_return_3m)} 3m
              </span>
            </>
          )}
          {article.win_rate_3m != null && (
            <>
              <span>&middot;</span>
              <span className="text-slate-500">{article.win_rate_3m.toFixed(0)}% win</span>
            </>
          )}
          <span className="ml-auto text-gray-400">{article.time_ago}</span>
        </div>
      </a>
      <div className="absolute top-2 right-2">
        <BookmarkButton
          itemType="trading"
          itemId={article.id}
          title={article.title}
          subtitle={article.primary_symbol ?? undefined}
          url={articleHref}
          size="sm"
        />
      </div>
    </div>
  );
}

export default function TradingSectionList(): ReactElement {
  const { section } = useParams<{ section: string }>();
  const [page, setPage] = useState(0);
  const limit = 20;

  const sectionConfig = SECTION_CONFIG[section || ''] || {
    name: section || 'Signals',
    icon: <Activity className="w-5 h-5" />,
    bgColor: 'bg-gray-500',
  };

  const { data, isLoading, error } = useQuery<TradingSectionListResponse>({
    queryKey: ['trading-section', section, page],
    queryFn: async () => {
      const response = await tradingAPI.getSectionArticles(section || '', {
        limit,
        offset: page * limit,
      });
      return response.data;
    },
    enabled: !!section,
    staleTime: 2 * 60 * 1000,
  });

  const batchItems = (data?.articles ?? []).map((a) => ({
    itemType: 'trading' as const,
    itemId: a.id,
  }));

  return (
    <BookmarkBatchProvider items={batchItems}>
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Back Link */}
        <Link
          to="/trading"
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Trading
        </Link>

        {/* Header */}
        <header className="mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div className={`w-10 h-10 rounded-lg ${sectionConfig.bgColor} flex items-center justify-center`}>
              <div className="text-white">{sectionConfig.icon}</div>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Georgia, serif' }}>
                {data?.name || sectionConfig.name}
              </h1>
            </div>
          </div>
        </header>

        {/* Loading */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400 mb-3" />
            <p className="text-sm text-gray-500">Loading articles...</p>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-50 flex items-center justify-center">
              <Activity className="w-8 h-8 text-red-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-700 mb-2">Failed to load articles</h3>
            <p className="text-sm text-gray-500">Please try again later</p>
          </div>
        )}

        {/* Articles */}
        {data && !isLoading && (
          <>
            <div className="space-y-3">
              {data.articles.map((article) => (
                <ArticleRow key={article.id} article={article} />
              ))}
            </div>

            {data.articles.length === 0 && (
              <div className="text-center py-20">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                  <Activity className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-700 mb-2">No articles yet</h3>
                <p className="text-sm text-gray-500">Check back later for new signals</p>
              </div>
            )}

            {/* Pagination */}
            <Pagination
              page={page}
              totalPages={Math.ceil(data.total_count / limit)}
              onPageChange={setPage}
              hasMore={data.has_more}
            />
          </>
        )}
      </div>
    </div>
    </BookmarkBatchProvider>
  );
}
